import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useSettings from '../../hooks/useSettings';
import Select from 'react-select';
import { getCustomSelectStyles } from '../../common/styles-select';
import apiClient from '../../config/apiClient';
import DashboardReport from './DashboardReport';
import Loading from '../../components/Loading';
import { useNotifications } from '../../contexts/NotificationsContext';

const ReportGenerator: React.FC = () => {
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [selectedTenantName, setSelectedTenantName] = useState<string | null>(null);
  const [tenants, setTenants] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshReport, setRefreshReport] = useState(false);
  const { themeMode } = useSettings();
  const { addNotification } = useNotifications();

  useEffect(() => {
    const fetchTenants = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get('/tenants');
        setTenants(response.data);
      } catch (error) {
        toast.error('Error fetching tenants');
      } finally {
        setLoading(false);
      }
    };
  
    fetchTenants();
  }, []);

  const handleTenantChange = (option: any) => {
    setSelectedTenant(option.value.toString());
    setSelectedTenantName(option.label);
  };

  const handleGenerateReport = async () => {
    if (!selectedTenant) return;
    setLoading(true);
    try {
      const response = await apiClient.get(`/usersStore/${selectedTenant}`);
      const data = response.data;
  
      if (data.error) {
        setLoading(false);
        toast.error('Error generating report');
        return;
      }
  
      setLoading(false);
      toast.success(`Report generation for ${selectedTenantName} has been queued. Please refresh periodically to monitor progress.`);
      setRefreshReport(prev => !prev);
      addNotification({
        id: new Date().toISOString(),
        message: `Report for ${selectedTenantName} has been queued.`,
        timestamp: new Date().toLocaleString(),
        status: 'queued',
      });
    } catch (error) {
      console.error('Error generating report:', error);
      setLoading(false);
      toast.error('Error generating report');
    }
  };

  const combinedSelectStyles = getCustomSelectStyles(themeMode);

  const tenantOptions = tenants.map((tenant) => ({
    value: tenant.id,
    label: tenant.name,
  }));

  const handleRefreshReport = () => {
    setRefreshReport(prev => !prev);
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6 text-center">SOC Billing Report</h1>

      <div className="flex justify-center items-center mb-6">
        <div className="mb-3 mr-4">
          <Select
            aria-label="Select tenant"
            placeholder="Select tenant"
            onChange={handleTenantChange}
            options={tenantOptions}
            styles={combinedSelectStyles}
            value={tenantOptions.find(option => option.value.toString() === selectedTenant)}
          />
        </div>
     
        <button
          className="bg-red-700 text-white font-bold py-2 px-4 rounded transition duration-300 
                    disabled:bg-gray-400 disabled:cursor-not-allowed"
          onClick={handleGenerateReport}
          disabled={loading || !selectedTenant}
        >
          {loading ? 'Generating...' : 'Generate Report'}
        </button>
      </div>

      {loading && (
        <Loading />
      )}

      <DashboardReport tenantId={selectedTenant} refreshSignal={refreshReport} handleRefresh={handleRefreshReport} />
    </div>
  );
};

export default ReportGenerator;
